import React from "react";

// Third Party
import { Navigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";

// Custom Components
import Loader from "../../components/UI/loader/Loader";
import MainLayout from "../main/MainLayout";

// Helpers
import { getLocalStorage } from "../../helpers/utils";

// Redux
import { RootState } from "../../store/store";

interface PrivateRouteProps {
  children: React.ReactNode;
  role?: string[];
  withMainLayout?: boolean;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({
  children,
  withMainLayout,
  role,
}) => {
  const { currentService } = useParams();

  if (
    currentService &&
    !["dime-rec", "dime-explain"].includes(currentService as string)
  ) {
    return <div>Page Not Found</div>;
  }

  const { user, isUserDataLoaded, loginDetails } = useSelector(
    (state: RootState) => state.user,
  );

  const token = getLocalStorage("token");

  if (!(token || loginDetails?.access_token)) {
    return <Navigate to="/login" />;
  }

  if (!isUserDataLoaded) {
    return <Loader page />;
  }

  const isTermsPage = window.location.pathname.includes("privacy-policy");
  if (!(user?.accept_terms && user?.accept_eula) && !isTermsPage) {
    return <Navigate to="/privacy-policy/" />;
  }

  const IS_SYSTEM_ADMIN =
    user?.roles?.findIndex((r) => r.name === "system_admin") !== -1;

  if (currentService === "select-service") {
    return <Navigate to="/dime-rec/dashboard/" />;
  }

  // Check for services permission for the user
  if (
    currentService &&
    !user?.services_assigned?.includes(
      (currentService as string).replaceAll("-", "_"),
    )
  ) {
    return <div>Page Not Found</div>;
  }

  // Check for role permission for the user
  if (
    !IS_SYSTEM_ADMIN &&
    role &&
    user?.roles?.findIndex((r) => role?.includes(r.name)) === -1
  ) {
    return <Navigate to="/dime-rec/dashboard/" />;
  }

  if (withMainLayout) {
    return (
      <MainLayout>
        <React.Suspense fallback={<Loader page />}>{children}</React.Suspense>
      </MainLayout>
    );
  }

  return <React.Suspense fallback={<Loader page />}>{children}</React.Suspense>;
};

export default PrivateRoute;
